import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import HandymanIcon from '@mui/icons-material/HandymanOutlined';
import OutboundIcon from '@mui/icons-material/OutboundOutlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupCountBy, groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import { withId } from '@/pages/reports/reportHelpers';
import type { JobWorkOrderRegisterRow, JobWorkPendingAtVendorRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76', '#3e5c8a'];

/** Job Work dashboard tab. Rendered only while ProcessConfiguration.JobWorkEnabled is on — the tab
 * itself is gated in Dashboard.tsx's CATEGORY_TABS. */
export default function JobWorkPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: orders = [], isLoading: loadingOrders } = useQuery({
    queryKey: ['reports', 'job-work-order-register'],
    queryFn: async () => (await apiClient.get<JobWorkOrderRegisterRow[]>('/reports/job-work-order-register')).data,
  });
  const { data: pending = [], isLoading: loadingPending } = useQuery({
    queryKey: ['reports', 'job-work-pending-at-vendor'],
    queryFn: async () => (await apiClient.get<JobWorkPendingAtVendorRow[]>('/reports/job-work-pending-at-vendor')).data,
  });

  const totalOrders = useMemo(() => new Set(orders.map((r) => r.jobWorkOrderId)).size, [orders]);
  const stillAtVendor = useMemo(() => pending.reduce((sum, r) => sum + r.pendingToReceiveQty, 0), [pending]);
  const receivedQty = useMemo(() => pending.reduce((sum, r) => sum + r.receivedQty, 0), [pending]);
  const rejectedQty = useMemo(() => pending.reduce((sum, r) => sum + r.rejectedQty, 0), [pending]);
  const overdueCount = useMemo(() => pending.filter((r) => r.isOverdue).length, [pending]);

  const sentVsReceived = useMemo(
    () => groupSumByMonth(pending, (r) => r.orderDate, [
      { key: 'expected', value: (r) => r.plannedQty },
      { key: 'received', value: (r) => r.receivedQty },
    ]),
    [pending],
  );
  const statusDistribution = useMemo(() => groupCountBy(orders, (r) => (r.isClosed ? 'Closed' : r.status)), [orders]);
  // What's actually sitting at vendors right now — the single thing this tab is watched for.
  const topPending = useMemo(
    () => topN(pending.filter((r) => r.pendingToReceiveQty > 0), (r) => r.pendingToReceiveQty, 10).reverse(),
    [pending],
  );

  const pendingColumns: DataTableColumn<ReturnType<typeof withId<JobWorkPendingAtVendorRow>>[number]>[] = [
    { field: 'orderNumber', headerName: tr('columns.number') },
    { field: 'vendorName', headerName: tr('columns.vendor') },
    { field: 'outputMaterialCode', headerName: tr('columns.outputMaterial') },
    { field: 'issuedQty', headerName: tr('columns.sentQty'), align: 'right' },
    { field: 'receivedQty', headerName: tr('columns.receivedQty'), align: 'right' },
    { field: 'pendingToReceiveQty', headerName: tr('columns.stillAtVendor'), align: 'right' },
    {
      field: 'ageInDays', headerName: tr('columns.age'), align: 'right',
      render: (r) => (r.isOverdue
        ? <Chip size="small" color="error" label={tr('columns.overdueDays', { days: r.ageInDays })} />
        : tr('columns.days', { days: r.ageInDays })),
    },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('jobWork.totalOrders')} value={totalOrders} icon={<HandymanIcon />} accentColor={colors.jobWork} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('jobWork.stillAtVendor')} value={stillAtVendor.toLocaleString()} icon={<OutboundIcon />} accentColor={colors.jobWork} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('jobWork.receivedQty')} value={receivedQty.toLocaleString()} icon={<MoveToInboxIcon />} accentColor={colors.production} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard
            label={overdueCount > 0 ? t('jobWork.overdueOrders') : t('jobWork.rejectedQty')}
            value={overdueCount > 0 ? overdueCount : rejectedQty.toLocaleString()}
            icon={<CancelOutlinedIcon />}
            accentColor={colors.adjustments}
          />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('jobWork.expectedVsReceivedTitle')} subtitle={t('jobWork.expectedVsReceivedSubtitle')} loading={loadingPending} empty={!loadingPending && sentVsReceived.length === 0}>
            <DashboardBarChart
              categories={sentVsReceived.map((m) => m.month)}
              series={[
                { label: tr('columns.expectedQty'), data: sentVsReceived.map((m) => m.values.expected), color: colors.procurement },
                { label: tr('columns.receivedQty'), data: sentVsReceived.map((m) => m.values.received), color: colors.jobWork },
              ]}
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('jobWork.statusTitle')} loading={loadingOrders} empty={!loadingOrders && statusDistribution.length === 0}>
            <DashboardPieChart data={statusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <ChartCard title={t('jobWork.pendingByOrderTitle')} loading={loadingPending} empty={!loadingPending && topPending.length === 0} height={300}>
        <DashboardBarChart
          layout="horizontal"
          categories={topPending.map((r) => r.orderNumber)}
          series={[{ label: tr('columns.stillAtVendor'), data: topPending.map((r) => r.pendingToReceiveQty), color: colors.jobWork }]}
          height={300}
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('jobWork.pendingTableTitle')}
        </Typography>
        <DataTable columns={pendingColumns} rows={withId(pending, (r) => r.jobWorkOrderId)} loading={loadingPending} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
    </Stack>
  );
}
